import { useMemo } from 'react';

import { KubernetesRbacFormValues } from '@/react/portainer/policies/components/kubernetes/rbac/types';
import { pluralize } from '@/react/common/string-utils';

import { FormSection } from '@@/form-components/FormSection';
import { Badge } from '@@/Badge';

import { PolicyEmptyState } from './PolicyEmptyState';
import { PolicyTableWrapper } from './PolicyTableWrapper';

interface Props {
  formData: KubernetesRbacFormValues;
}

/**
 * Read-only table display of RBAC policy access list with clean visual styling.
 */
export function RBACPolicyReadOnly({ formData }: Props) {
  const { userCount, teamCount } = useMemo(() => {
    const list = formData.accessList || [];
    return {
      userCount: list.filter((a) => a.Type === 'user').length,
      teamCount: list.filter((a) => a.Type === 'team').length,
    };
  }, [formData.accessList]);

  const accessList = formData.accessList || [];

  if (accessList.length === 0) {
    return (
      <PolicyEmptyState
        title="Access List"
        message="No access configured for this policy."
      />
    );
  }

  return (
    <FormSection title="Access List">
      <PolicyTableWrapper
        caption="RBAC access list showing users and teams with their roles and namespace permissions"
        data-cy="rbac-policy-readonly-table"
      >
        <thead className="bg-gray-3 th-dark:bg-gray-9">
          <tr>
            <th className="px-4 py-3 text-left text-sm font-medium">Name</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Type</th>
            <th className="px-4 py-3 text-left text-sm font-medium">Role</th>
            <th className="px-4 py-3 text-left text-sm font-medium">
              Namespaces
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-5 th-dark:divide-gray-7">
          {accessList.map((access) => (
            <tr
              key={`${access.Type}-${access.Id}`}
              className="bg-gray-2 th-dark:bg-gray-iron-10"
            >
              <td className="px-4 py-3 text-sm font-medium">{access.Name}</td>
              <td className="px-4 py-3">
                <Badge type={access.Type === 'user' ? 'info' : 'warn'}>
                  {access.Type === 'user' ? 'User' : 'Team'}
                </Badge>
              </td>
              <td className="px-4 py-3 text-sm">{access.Role.Name}</td>
              <td className="px-4 py-3 text-sm">
                {access.namespaces && access.namespaces.length > 0 ? (
                  <span className="font-medium">
                    {access.namespaces.join(', ')}
                  </span>
                ) : (
                  <span className="text-muted italic">All namespaces</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </PolicyTableWrapper>
      <div className="mt-3 text-sm text-muted">
        Total: <span className="font-medium">{userCount}</span>{' '}
        {pluralize(userCount, 'user')},{' '}
        <span className="font-medium">{teamCount}</span>{' '}
        {pluralize(teamCount, 'team')}
      </div>
    </FormSection>
  );
}
